/**
 * The authoritative game state.
 *
 * There is exactly one of these per session, it lives in the server process,
 * and **only tick-time code mutates it**. Everything reaching it from outside
 * arrives as a `ClientIntent` on the `GameLoop` queue — today from a bot in the
 * same process, later from a WebSocket. See `server/src/tick.ts`.
 *
 * Mutable classes rather than immutable records: this state is stepped ten times
 * a second for tens of thousands of ticks, and allocating a fresh 1600-hex map
 * each tick would dominate the tick budget. The discipline that keeps that safe
 * is the seam, not immutability — nothing outside a tick holds a reference long
 * enough to observe a torn state.
 */
import { hexKey, neighbors } from "kngdm-shared";
export function createHexRecord(coord, biome, special = null) {
    return {
        coord,
        key: hexKey(coord),
        biome,
        special,
        owner: null,
        building: null,
        troops: 0,
        capture: 0,
        captureBy: null,
        fortifiedUntilTick: null,
        version: 0,
    };
}
export function toHexState(record, tick, eventAffected) {
    return {
        coord: record.coord,
        biome: record.biome,
        owner: record.owner,
        building: record.building,
        special: record.special,
        fortified: isFortified(record, tick),
        eventAffected,
        troops: round2(record.troops),
        capture: round3(record.capture),
    };
}
/** True while a `fortify` god tool is still running on this hex. */
export function isFortified(record, tick) {
    return record.fortifiedUntilTick !== null && tick < record.fortifiedUntilTick;
}
export function toKingdomState(record) {
    return {
        id: record.id,
        name: record.name,
        color: record.color,
        resources: {
            food: round2(record.resources.food),
            timber: round2(record.resources.timber),
            ore: round2(record.resources.ore),
            gold: round2(record.resources.gold),
            aether: round2(record.resources.aether),
        },
        workerRatio: record.workerRatio,
        population: round2(record.population),
        eliminated: record.eliminated,
        isBot: record.isBot,
        surges: [...record.surges],
    };
}
// ---------------------------------------------------------------------------
// Accessors
//
// Everything below is a read helper. Deliberately no mutation helpers here —
// mutation belongs to the simulation step, and putting a `claimHex()` on the
// state object would make it far too easy to call from outside a tick.
// ---------------------------------------------------------------------------
export function getHex(state, coord) {
    return state.hexes.get(hexKey(coord));
}
export function getBiome(state, coord) {
    return state.hexes.get(hexKey(coord))?.biome ?? null;
}
export function getOwner(state, coord) {
    return state.hexes.get(hexKey(coord))?.owner ?? null;
}
export function markDirty(state, record) {
    record.version += 1;
    state.dirtyHexes.add(record.key);
}
/** In-map neighbours of a hex. Off-map coordinates are simply absent. */
export function hexNeighbourRecords(state, coord) {
    const result = [];
    for (const n of neighbors(coord)) {
        const record = state.hexes.get(hexKey(n));
        if (record !== undefined)
            result.push(record);
    }
    return result;
}
/** Owner of each neighbour keyed by hexKey, in the shape `checkClaim` wants. */
export function neighbourOwnerMap(state, coord) {
    const owners = new Map();
    for (const n of neighbors(coord)) {
        const key = hexKey(n);
        const record = state.hexes.get(key);
        owners.set(key, record?.owner ?? null);
    }
    return owners;
}
/**
 * A hex is on the frontier if it touches land owned by someone else. Those are
 * the only hexes that hold troops, and the only ones combat is resolved on.
 */
export function isFrontierHex(state, record) {
    if (record.owner === null)
        return false;
    for (const neighbour of hexNeighbourRecords(state, record.coord)) {
        if (neighbour.owner !== record.owner)
            return true;
    }
    return false;
}
export function kingdomHexRecords(state, kingdomId) {
    const kingdom = state.kingdoms.get(kingdomId);
    if (kingdom === undefined)
        return [];
    const records = [];
    for (const key of kingdom.hexes) {
        const record = state.hexes.get(key);
        if (record !== undefined)
            records.push(record);
    }
    return records;
}
export function livingKingdoms(state) {
    return [...state.kingdoms.values()].filter((k) => !k.eliminated);
}
// ---------------------------------------------------------------------------
// Serialisation
// ---------------------------------------------------------------------------
export function toMapState(state) {
    const affected = eventAffectedKeys(state);
    return {
        seed: state.seed,
        width: state.width,
        height: state.height,
        hexes: [...state.hexes.values()].map((record) => toHexState(record, state.tick, affected.has(record.key))),
    };
}
/** Every hex key currently under at least one world event. */
export function eventAffectedKeys(state) {
    const keys = new Set();
    for (const active of state.activeEvents) {
        for (const key of active.hexKeys)
            keys.add(key);
    }
    return keys;
}
function round2(value) {
    return Math.round(value * 100) / 100;
}
function round3(value) {
    return Math.round(value * 1000) / 1000;
}
//# sourceMappingURL=state.js.map